/* eslint-disable camelcase */
import { useState } from "react";
import { FileObject } from "material-ui-dropzone";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { AppState } from "../../redux";

/**
 * @Interface for membership to be added form data.
 */
export interface MembershipAddFormData {
  first_name: string;
  last_name: string;
  email: string;
  username: string;
  birthday: string;
  street: string;
  street_number: string;
  zip_code: string;
  city: string;
  country: string;
  organization_id: string;
  status?: string;
}

// Email validation regex pattern
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const birthdateRegex = /^(0[1-9]|[12][0-9]|3[01])\.(0[1-9]|1[0-2])\.\d{4}$/;

/**
 * A custom hook for validating CSV files containing membership user data.
 * @returns {{
 *  loading: boolean,
 *  validMembersToAdd: MembershipAddFormData[],
 *  invalidUserData: string[],
 *  validateCsv: (csvFiles: FileObject[], organizationId: string) => Promise<void>,
 *  setValidMembersToAdd: React.Dispatch<React.SetStateAction<MembershipAddFormData[]>>
 * }} The custom hook for CSV validation.
 */
export default function useCsvUserAddValidation(addUserWithoutMail: boolean) {
  const { t } = useTranslation(["organization"]);
  const [loading, setLoading] = useState(false);
  const [validUserData, setValidUserData] = useState<MembershipAddFormData[]>([]);
  const [invalidUserData, setInvalidUserData] = useState<string[]>([]);
  const addressRequired = useSelector(
    (state: AppState) => state.metaData.metaData.address_required
  );
  const emails = new Set<string>();
  /**
   * Checks the validity of the format of the CSV data.
   * @param {string[]} columnData - The data in each column of the CSV row.
   * @param {number} rowIndex - The index of the CSV row being validated.
   * @param {string} organizationId - The organization ID associated with the user data.
   */
  const checkFormatValidity = (
    columnData: string[],
    rowIndex: number,
    organizationId: string
  ) => {
    // Check if all columns are empty, if so, skip this row
    if (columnData.every((col) => !col.trim())) {
      return;
    }

    const errorMessages: string[] = [];

    // Check for error fields
    if (!columnData[0]?.trim()) {
      errorMessages.push(t("csv.firstNameRequired", { ns: "organization" }));
    }
    if (!columnData[1]?.trim()) {
      errorMessages.push(t("csv.lastNameRequired", { ns: "organization" }));
    }
    if (!addUserWithoutMail) {
      if (!columnData[2]?.trim()) {
        errorMessages.push(t("csv.emailRequired", { ns: "organization" }));
      } else if (!emailRegex.test(columnData[2])) {
        errorMessages.push(t("csv.emailInvalid", { ns: "organization" }));
      } else if (emails.has(columnData[2].toLowerCase())) {
        errorMessages.push(t("csv.emailDuplicate", { ns: "organization" }));
      }
    }
    const birthdateValue = columnData[4]?.trim() ?? "";
    if (!birthdateValue) {
      errorMessages.push(t("dates.birthdateRequired", { ns: "validations" }));
    } else if (!birthdateRegex.test(birthdateValue)) {
      errorMessages.push(t("dates.birthdateInvalid", { ns: "validations" }));
    }

    if (addressRequired) {
      if (!columnData[5]?.trim()) {
        errorMessages.push(t("streetRequired", { ns: "validations" }));
      }
      if (!columnData[6]?.trim()) {
        errorMessages.push(t("streetNumberRequired", { ns: "validations" }));
      }
      if (!columnData[7]?.trim()) {
        errorMessages.push(t("zipCode.zipCodeRequired", { ns: "validations" }));
      }
      if (!columnData[8]?.trim()) {
        errorMessages.push(t("cityRequired", { ns: "validations" }));
      }
      if (!columnData[9]?.trim()) {
        errorMessages.push(t("countryRequired", { ns: "validations" }));
      }
    }

    if (errorMessages.length > 0) {
      setInvalidUserData((prev) => [
        ...prev,
        `${t("csv.row", { ns: "organization" })} ${rowIndex + 1}: ${errorMessages.join(" ")} (${columnData.join(" ")})\n`,
      ]);
    } else {
      emails.add(columnData[2].toLowerCase());
      setValidUserData((prev) => [
        ...prev,
        {
          first_name: columnData[0],
          last_name: columnData[1],
          email: columnData[2] ?? "",
          username: columnData[3] ?? "",
          birthday: columnData[4],
          street: addressRequired ? columnData[5] : "",
          street_number: addressRequired ? columnData[6] : "",
          zip_code: addressRequired ? columnData[7] : "",
          city: addressRequired ? columnData[8] : "",
          country: addressRequired ? columnData[9] : "",
          organization_id: organizationId,
          status: "",
        },
      ]);
    }
  };

  /**
   * Validates the CSV files containing membership user data.
   * @param {FileObject[]} csvFiles - The CSV files to be validated.
   * @param {string} organizationId - The organization ID associated with the user data.
   */
  const validateCsv = async (csvFiles: FileObject[], organizationId: string) => {
    setInvalidUserData([]);
    setLoading(true);
    if (csvFiles?.length > 0) {
      const reader = new FileReader();
      const file = csvFiles[0].file;
      reader.onload = (event) => {
        const csvData = event.target?.result as string;
        const cleanedCsvData = csvData.replace(/\r/g, "");
        const rows = cleanedCsvData.split("\n"); // Split by newline to get rows

        // Detect if the first row is a header
        let startRow = 0;
        const firstRowColumns = rows[0].split(/[;,]/).map((col) => col.trim());
        const headerColumnsBasic = [
          "name",
          "vorname",
          "email",
          "benutzername",
          "geburtsdatum",
        ];
        const headerColumnsExtended = [
          ...headerColumnsBasic,
          "straße",
          "hausnummer",
          "plz",
          "stadt",
          "land",
        ];
        if (
          (addressRequired &&
            headerColumnsExtended.every((col) => firstRowColumns.includes(col))) ||
          (!addressRequired &&
            headerColumnsBasic.every((col) => firstRowColumns.includes(col)))
        ) {
          // If the first row contains header columns, skip it
          startRow = 1;
        }

        // Track if any valid rows are found
        let hasValidRows = false;

        // Start processing from the detected row
        rows.slice(startRow).forEach((row, index) => {
          // Split by comma or semicolon and trim whitespace
          const columns = row.split(/[;,]/).map((col) => col.trim());
          if (columns.some((col) => col.trim() !== "")) {
            // Only check rows that are not completely empty
            checkFormatValidity(columns, index + startRow, organizationId);
            hasValidRows = true;
          }
        });
        if (!hasValidRows) {
          setInvalidUserData([t("csv.emptyFile", { ns: "organization" })]);
        }
      };
      reader.readAsText(file);
    }
    setLoading(false);
  };

  return {
    loading,
    validUserData,
    invalidUserData,
    validateCsv,
    setValidUserData,
  };
}
